import { useLocation } from "wouter";
import { ChevronIcon } from "../design/Icons";
import { OrPill } from "../design/OrPill";
import { SIDES, OptionSideKey, palette } from "../design/tokens";
import { t } from "../locale";

/**
 * plan.md §14: a how-to-play page reachable from the lobby and the menu,
 * doubling as landing copy.
 *
 * It shows the split rather than describing it — the device is the rules, and
 * a player who has seen it once here recognises it on the turn screen.
 */

const Step = ({ index, body }: { index: number; body: string }) => (
  <li className="flex gap-3">
    <span className="w-5 shrink-0 text-right text-base font-bold tabular-nums text-ink-dim">
      {index}
    </span>
    <span className="text-base leading-snug">{body}</span>
  </li>
);

export const HowToPlay = () => {
  const [, navigate] = useLocation();

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-5 pb-8 pt-6">
      <button
        onClick={() => navigate("/")}
        className="flex min-h-0 min-w-0 items-center gap-1 self-start py-2 pr-2 text-sm text-ink-dim"
      >
        <ChevronIcon direction="left" color={palette.inkDim} size={16} />
        {t.auth.back}
      </button>

      <h1 className="mt-4 text-2xl">{t.howTo.title}</h1>

      {/*
        A miniature of the real device, at rest — inset rather than bleeding to
        the edges, so it reads as an illustration of the screen rather than as
        the screen. No side markers, matching the live turn and reveal cards.
      */}
      <div className="mt-6 px-6">
        <div className="relative h-40 overflow-hidden rounded-xl">
          {([OptionSideKey.A, OptionSideKey.B] as const).map((key) => (
            <div
              key={key}
              className="flex h-1/2 w-full items-center px-4"
              style={{
                backgroundColor: SIDES[key].color,
                color: SIDES[key].ink,
              }}
            >
              <span className="text-[13px] font-bold leading-snug">
                {key === OptionSideKey.A ? t.howTo.exampleA : t.howTo.exampleB}
              </span>
            </div>
          ))}
          <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center">
            <OrPill size={12} />
          </div>
        </div>
      </div>

      <ol className="mt-6 flex flex-col gap-4">
        <Step index={1} body={t.howTo.step1} />
        <Step index={2} body={t.howTo.step2} />
        <Step index={3} body={t.howTo.step3} />
        <Step index={4} body={t.howTo.step4} />
        <Step index={5} body={t.howTo.step5} />
        <Step index={6} body={t.howTo.scoring} />
      </ol>

      <p className="mt-8 text-sm text-ink-dim">{t.howTo.noAccount}</p>

      {/* Back, not "create a room" — the landing screen holds the Turnstile
          token, so starting a room from here would jump over verification. */}
      <button
        className="mt-8 w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg"
        onClick={() => navigate("/")}
      >
        {t.auth.back}
      </button>
    </div>
  );
};
